import { supabaseClient } from "@/supabase/app";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
// import { dispatch } from "@redux/store";
import { enqueueSnackbar } from "notistack";
import { LoginData, RegisterData } from "@/schemas/auth";
import PATHS from "@routes/paths";
import { TAuthInit, TUser } from "@typedefs/auth";
import { Tables, TablesInsert } from "@/types/supabase";
import { store } from "@redux/store";

type TAuthState = {
  isInitialized: boolean;
  isAuthenticated: boolean;
  user?: TUser;
};

// ----------------------------------------------------------------------
const initialState: TAuthState = {
  isInitialized: false,
  isAuthenticated: false,

  user: undefined,
};
export interface UserAccount {
  id: number;
  first_name: string;
  last_name: string;
  password?:string;
  profile_type?:string;
  email: string;
  phone_number?: string | null;
  photo_url?: string | null;
  role: string;
  server?: string;
  created_at: string; // Alternatively, Date can be used if you prefer handling it as a Date object
  apikey:string;
}

export interface IUser {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  created_at: string;
}

const slice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    // called on every user state change, eg. login, reg, logout
    init: (state: TAuthState, action: PayloadAction<TAuthInit>) => {
      const { user } = action.payload;
      state.user = user;
      state.isInitialized = true;
      state.isAuthenticated = !!user;
    },
    updateUser: (state: TAuthState, action: PayloadAction<{ user: TUser }>) => {
      const { user } = action.payload;
      state.user = user;
    },

    logout: (state: TAuthState) => {
      state.isAuthenticated = false;
      state.isInitialized = true;
      state.user = undefined;
    },
  },
});

export default slice.reducer;
export const updateUserRes = slice.actions.updateUser;
export const { init } = slice.actions;

// ----------------------------------------------------------------------
export function refreshToken() {
  return async () => {
    try {
      const { data } = await supabaseClient.auth.getUser();

      if (data.user?.id) {
        const { data: UserData, error } = await supabaseClient
          .from("user")
          .select("*")
          .eq("email", data.user?.email)
          .single();

        if (error) {
          throw error;
        }
        store.dispatch(slice.actions.init({ user: UserData as TUser }));
      } else {
        throw Error("No Session found");
      }
    } catch (error) {
      // ---LOGOUT USER-----------------------------
      store.dispatch(slice.actions.logout());
    }
  };
}

// ---REGISTER USER-----------------------------
export async function register(
  data: RegisterData,
  navigate: (path: string) => void
) {

  console.log(data)
  console.log("I am in register")
  try {
    // ---LOGIN USER-----------------------------
    const { data: returnData, error } = await supabaseClient.auth.signUp({
      email: data.email,
      password: data.password,
      // apikey:data.apikey,
      options: {
        data: {
          firstName: data.firstName,
          lastName: data.lastName,
        },
      },
    });

    if (error) {
      throw error;
    }

    const { data: userData, error: errorUser } = await supabaseClient
      .from("user")
      .insert<TablesInsert<"user">>({
        email: returnData?.user?.email ?? "",
        first_name: returnData.user?.user_metadata?.firstName,
        last_name: returnData.user?.user_metadata?.firstName,
        apikey:data.apikey,
      })
      .select("*")
      .single();

    if (errorUser) {
      throw errorUser;
    }

    // store.dispatch(slice.actions.init({ user: userData as TUser }));

    // navigate(PATHS.login);

    enqueueSnackbar("Account created succesfully", {
      variant: "success",
    });
  } catch (error) {
    enqueueSnackbar(error.message, { variant: "error" });
  }
}

// ---LOGIN USER-----------------------------
export async function login(email: string, password: string, navigate: (path: string) => void) {
  try {
    if (email === "superadmin@gmail.com" || email === "csm@aquisize.de") {
      // Directly log in the superadmin without sending OTP
      const { data: userData, error: userError } = await supabaseClient
        .from("user")
        .select("*")
        .eq("email", email)
        .single();

      if (userError || !userData) {
        throw new Error("User not found.");
      }

      store.dispatch(slice.actions.init({ user: userData as TUser }));
      enqueueSnackbar("Logged in successfully!", { variant: "success" });
      navigate("/dashboard");
      return; // Exit the function after successful login
    }

    // Send OTP to the user's email
    const { error } = await supabaseClient.auth.signInWithOtp({
      email: email,
      options: {
        shouldCreateUser: false,
      },
    });

    if (error) {
      throw error;
    }

    enqueueSnackbar(
      "A verification code has been sent to your email. Please check your inbox.",
      { variant: "info" }
    );
  } catch (error: any) {
    enqueueSnackbar(error.message || "An error occurred during login.", {
      variant: "error",
    });
  }
}

// 
export async function checkEmailExists(email: string) {
  try {
    const { data, error } = await supabaseClient
      .from("user") // Replace with your actual user table name
      .select("*")
      .eq("email", email)
      .single(); // Use single to get only one record

    // If there's an error, or if no user data is returned, the email doesn't exist
    if (error || !data) {
      return false; // Email does not exist
    }

    return true; // Email exists
  } catch (error) {
    console.error("Error checking email:", error);
    return false; // Handle any unexpected errors
  }
}

// 

export async function verifyOtp(
  email: string,
  otpCode: string,
  navigate: (path: string) => void
) {
  try {
    // Call Supabase to verify the OTP
    const { data, error } = await supabaseClient.auth.verifyOtp({
      email: email,
      token: otpCode,
      type: "email", // Specify that this is an email OTP
    });

    if (error) {
      throw new Error("Invalid OTP or OTP expired.");
    }

    // If OTP is verified, get the user data
    const { data: userData, error: userError } = await supabaseClient
      .from("user")
      .select("*")
      .eq("email", email)
      .single();

    if (userError || !userData) {
      throw new Error("User not found.");
    }

    // Update the Redux store and navigate to the dashboard
    store.dispatch(slice.actions.init({ user: userData as TUser }));
    enqueueSnackbar("Logged in successfully!", { variant: "success" });
    navigate("/dashboard");
  } catch (error: any) {
    enqueueSnackbar(error.message || "An error occurred during OTP verification.", {
      variant: "error",
    });
    throw error;
  }
}

export async function loginWithGoogle() {
  try {
    const { error } = await supabaseClient.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: import.meta.env.VITE_APP_BASE_URL_UI + PATHS.validate,
      },
    });

    if (error) {
      throw error;
    }

    const { data: userSession } = await supabaseClient.auth.getUser();

    if (userSession.user?.id) {
      const { data: userData, error: errorUser } = await supabaseClient
        .from("user")
        .select("*")
        .eq("email", userSession.user?.email)
        .single();

      if (errorUser && userSession.user?.email) {
        await supabaseClient.from("user").insert<TablesInsert<"user">>({
          email: userSession.user?.email,
          first_name: userSession.user?.user_metadata?.firstName,
          last_name: userSession.user?.user_metadata?.lastName,
        });
      }
    }

    store.dispatch(refreshToken());
    enqueueSnackbar("Logged in successfully", { variant: "success" });
  } catch (error) {
    console.error("error", error);
    enqueueSnackbar(error.message, { variant: "error" });
  }
}

// export function updateUser(action: User.UpdateReq) {
//   return async () => {
//     try {
//       const { data } = await fn.userUpdate(action);
//       analytics.user.update();
//       store.dispatch(slice.actions.updateUser(data));
//       return data.user;
//     } catch (e) {
//       throw e;
//     }
//   };
// }

export function logout() {
  return async () => {
    console.log("logout");
    await supabaseClient.auth.signOut();
    store.dispatch(slice.actions.logout());
  };
}

export async function getUsers(): Promise<UserAccount[] | null> {
//   userId: string
  try {
    const { data, error } = await supabaseClient.from("user").select("*");
    //   .eq("user_id", userId);

    if (error) {
      throw error;
    }
    const filteredData = data?.filter((i) => i.role !== "admin");
    console.log(filteredData)
    return filteredData as UserAccount[];
  } catch (error) {
    enqueueSnackbar(`Error fetching users accounts: ${error.message}`, {
      variant: "error",
    });
    return null;
  }
}


export async function deleteUser(userId: string): Promise<boolean> {

  console.log(userId)
  console.log("User id is this")
  try {
    const { error } = await supabaseClient
      .from("user")
      .delete()
      .eq("id", userId); 

    if (error) {
      throw error;
    }
    enqueueSnackbar("User successfully deleted", { variant: "success" });
    return true;
  } catch (error) {
    console.log(error.message)
    enqueueSnackbar(`Error deleting user: ${error.message}`, {
      variant: "error",
    });
    return false;
  }
}

//
// Deleting USER ROUTINES
export async function deleteUserFromBMA(userId: string): Promise<boolean> {


  try {
    const { error } = await supabaseClient
      .from("bma_profiles")
      .delete()
      .eq("user_id", userId); 

    if (error) {
      throw error;
    }

    // enqueueSnackbar("User successfully deleted", { variant: "success" });
    return true;
  } catch (error) {
    console.log(error.message)
    return false;
  }


}
export async function deleteUserFromCIC(userId: string): Promise<boolean> {

 
  try {
    const { error } = await supabaseClient
      .from("cic_profiles")
      .delete()
      .eq("user_id", userId); 

    if (error) {
      throw error;
    }

    // enqueueSnackbar("User successfully deleted", { variant: "success" });
    return true;
  } catch (error) {
    console.log(error.message)
    
    return false;
  }


}


export async function deleteUserFromDomain(userId: string): Promise<boolean> {

  try {
    const { error } = await supabaseClient
      .from("domains")
      .delete()
      .eq("user_id", userId); 

    if (error) {
      throw error;
    }

    // enqueueSnackbar("User successfully deleted", { variant: "success" });
    return true;
  } catch (error) {
    console.log(error.message)
    
    return false;
  }


}
export async function deleteUserFromEmail(userId: string): Promise<boolean> {

  try {
    const { error } = await supabaseClient
      .from("emailAccounts")
      .delete()
      .eq("user_id", userId); 

    if (error) {
      throw error;
    }

    // enqueueSnackbar("User successfully deleted", { variant: "success" });
    return true;
  } catch (error) {
    console.log(error.message)
    
    return false;
  }


}
export async function deleteUserFromICP(userId: string): Promise<boolean> {

  try {
    const { error } = await supabaseClient
      .from("icp_profiles")
      .delete()
      .eq("user_id", userId); 

    if (error) {
      throw error;
    }

    // enqueueSnackbar("User successfully deleted", { variant: "success" });
    return true;
  } catch (error) {
    console.log(error.message)
    
    return false;
  }
}


export async function deleteUserFromInvited(userId: string): Promise<boolean> {

  try {
    const { error } = await supabaseClient
      .from("invited_members")
      .delete()
      .eq("user_id", userId); 

    if (error) {
      throw error;
    }

    // enqueueSnackbar("User successfully deleted", { variant: "success" });
    return true;
  } catch (error) {
    console.log(error.message)
    
    return false;
  }
}

export async function deleteUserFromPublished(userId: string): Promise<boolean> {

  
  try {
    const { error } = await supabaseClient
      .from("published_templates")
      .delete()
      .eq("user_id", userId); 

    if (error) {
      throw error;
    }

    // enqueueSnackbar("User successfully deleted", { variant: "success" });
    return true;
  } catch (error) {
    console.log(error.message)
    
    return false;
  }
}

export async function deleteUserFromSaved(userId: string): Promise<boolean> {

  try {
    const { error } = await supabaseClient
      .from("saved_email_templates")
      .delete()
      .eq("user_id", userId); 

    if (error) {
      throw error;
    }

    // enqueueSnackbar("User successfully deleted", { variant: "success" });
    return true;
  } catch (error) {
    console.log(error.message)
    
    return false;
  }
}

export async function deleteUserFromSetting(userId: string): Promise<boolean> {

  try {
    const { error } = await supabaseClient
      .from("settings")
      .delete()
      .eq("user_id", userId); 

    if (error) {
      throw error;
    }

    // enqueueSnackbar("User successfully deleted", { variant: "success" });
    return true;
  } catch (error) {
    console.log(error.message)
    
    return false;
  }
}

export async function deleteUserFromVPS(userId: string): Promise<boolean> {

  try {
    const { error } = await supabaseClient
      .from("vps_profiles")
      .delete()
      .eq("user_id", userId); 

    if (error) {
      throw error;
    }

    // enqueueSnackbar("User successfully deleted", { variant: "success" });
    return true;
  } catch (error) {
    console.log(error.message)
    
    return false;
  }
}

export async function deleteUserFromTargets(userId: string): Promise<boolean> {

  try {
    const { error } = await supabaseClient
      .from("targetMarkets")
      .delete()
      .eq("user_id", userId); 

    if (error) {
      throw error;
    }

    // enqueueSnackbar("User successfully deleted", { variant: "success" });
    return true;
  } catch (error) {
    console.log(error.message)
    
    return false;
  }
}


export async function updateUserApiKey(userId: any, newApiKey: string): Promise<boolean> {
  try {
    const { error } = await supabaseClient
      .from("user")
      .update({ apikey: newApiKey })
      .eq("id", userId);

    if (error) {
      throw error;
    }

    // enqueueSnackbar("API key successfully updated", { variant: "success" });
    return true;
  } catch (error) {
    console.log(error.message);
    return false;
  }
}
//


// --- Function to fetch user details by ID ---
export async function getUserById(userId: number): Promise<UserAccount | null> {
  try {
    const { data, error } = await supabaseClient
      .from("user")
      .select("*")
      .eq("id", userId)
      .single(); // Use single() to get a single user

    if (error) {
      throw error;
    }

    return data as UserAccount; // Return the user data
  } catch (error) {
    enqueueSnackbar(`Error fetching user details: ${error.message}`, {
      variant: "error",
    });
    return null; // Return null if there's an error
  }
} // Closing brace added here
